// src/pages/MechanicsDashboardHome.jsx
import React, { useState, useEffect, useCallback, useRef } from "react";
// 🚀 MODERN CHARTING LIBRARY IMPORTS
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
} from "recharts";

import { HiOutlineCurrencyRupee } from "react-icons/hi";
import {
  FaUsers,
  FaTruck,
  FaBoxes,
  FaChartLine,
  FaFileInvoiceDollar,
  FaShoppingCart,
  FaCalendarAlt,
  FaRedoAlt,
  FaTimes,
  FaCheckCircle,
  FaExclamationTriangle,
  FaSun,
  FaMoon,
  FaLanguage,
  FaPalette,
  FaFilter,
  FaWrench,
  FaExchangeAlt,
  FaUserCog,
  FaArrowCircleLeft,
  FaMoneyBillWave,
  FaChartPie,
  FaSlidersH,
  FaGift,
  FaWallet,
  FaCreditCard,
} from "react-icons/fa";
import { MdAttachMoney, MdInventory, MdCategory, MdOutlineAnalytics, MdDiscount } from "react-icons/md";
import { FiTrendingUp, FiAlertCircle, FiClock, FiPackage, FiTrash2, FiPercent } from "react-icons/fi";
import { useAuth } from '../module_roots/AuthContext';
import {
  fetchDashboardData,
  setAuthData,
  fetchServicesData,
  fetchMobchangeData,
  fetchReturnsData,
  fetchEmployeesData,
  fetchTransactionsData,
  fetchPurchasesData,
  fetchProductStats,
} from "./pharmacy_api.js";
import "../module_mechanics/Styles/DashHomeUi.css";

// ==================== PAKISTANI CURRENCY FORMATTER ====================
const formatPKR = (value) => {
  const num = parseFloat(value);
  if (isNaN(num) || num === null || num === undefined) {
    return "₨0";
  }
  return new Intl.NumberFormat("en-PK", {
    style: "currency",
    currency: "PKR",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  })
    .format(num)
    .replace("PKR", "₨");
};

// ==================== TRANSLATION SYSTEM ====================
const createDashboardTranslator = () => {
  let cache = new Map();

  const loadFromCache = () => {
    try {
      const cached = localStorage.getItem("ourzest_dashboard_translations");
      if (cached) {
        const parsed = JSON.parse(cached);
        Object.entries(parsed).forEach(([key, value]) => {
          cache.set(key, value);
        });
      }
    } catch (e) {
      console.log("No cache found for Dashboard");
    }
  };

  const saveToCache = () => {
    try {
      const cacheObj = {};
      cache.forEach((value, key) => {
        cacheObj[key] = value;
      });
      localStorage.setItem("ourzest_dashboard_translations", JSON.stringify(cacheObj));
    } catch (e) {
      console.error("Error saving to cache:", e);
    }
  };

  const translateAPI = async (text, targetLang) => {
    if (targetLang === "en") return text;

    const dict = {
      ur: {
        "Analytics Dashboard": "تجزیاتی ڈیش بورڈ",
        Welcome: "خوش آمدید",
        Services: "خدمات",
        Mobchange: "موبائل تبدیلی",
        Returns: "واپسی",
        Employees: "ملازمین",
        Transactions: "لین دین",
        "Recent Services": "حالیہ خدمات",
        "Recent Mobchanges": "حالیہ موبائل تبدیلیاں",
        "Recent Returns": "حالیہ واپسیاں",
        "Recent Employees": "حالیہ ملازمین",
        "Customer Transactions": "کسٹمر لین دین",
        "Supplier Transactions": "سپلائر لین دین",
        "Employee Payments": "ملازمین کی ادائیگیاں",
        "Expense Transactions": "اخراجات کے لین دین",
        "Total Services": "کل خدمات",
        "Total Mobchanges": "کل موبائل تبدیلیاں",
        "Total Returns": "کل واپسیاں",
        "Total Employees": "کل ملازمین",
        "Service Revenue": "خدمات سے آمدنی",
        "Mobchange Revenue": "موبائل تبدیلی سے آمدنی",
        "Total Revenue": "کل آمدنی",
        "Recent Sales": "حالیہ فروخت",
        "Recent Purchases": "حالیہ خریداری",
        "Low Stock Alerts": "کم اسٹاک الرٹس",
        "Overall Sales": "مجموعی فروخت",
        "Total Customers": "کل گاہک",
        Suppliers: "سپلائرز",
        Products: "پروڈکٹس",
        "Inventory Value": "انوینٹری ویلیو",
        "Low Stock Alert": "کم اسٹاک الرٹ",
        "Expired Products": "میعاد ختم شدہ مصنوعات",
        "Out of Stock": "اسٹاک سے باہر",
        "Disposed Products": "ضائع شدہ مصنوعات",
        "Discount Products": "ڈسکاؤنٹ مصنوعات",
        "Bonus Products": "بونس مصنوعات",
        "Expiring Soon": "جلد میعاد ختم",
        Pending: "زیر التواء",
        Completed: "مکمل",
        "In Progress": "جاری ہے",
        Paid: "ادا شدہ",
        Unpaid: "غیر ادا شدہ",
        Partial: "جزوی",
        "Last Week": "پچھلے ہفتے",
        "Last Month": "پچھلے مہینے",
        "Last 6 Months": "پچھلے 6 مہینے",
        "Custom Date": "اپنی تاریخ",
        "Filter by Date": "تاریخ کے مطابق فلٹر کریں",
        "Advanced Sections": "اعلیٰ حصے",
        "Date Range": "تاریخ کی حد",
        "From": "سے",
        "To": "تک",
        "Apply Filter": "فلٹر لگائیں",
        "Clear Filter": "فلٹر صاف کریں",
        "Customer Paid": "کسٹمر کی ادائیگی",
        "Customers Balances": "کسٹمر کے بیلنس",
      },
      ar: {
        "Analytics Dashboard": "لوحة التحليل",
        Welcome: "مرحباً",
        Services: "الخدمات",
        Mobchange: "تغيير الجوال",
        Returns: "المرتجعات",
        Employees: "الموظفين",
        Transactions: "المعاملات",
        "Recent Services": "الخدمات الأخيرة",
        "Recent Mobchanges": "تغييرات الجوال الأخيرة",
        "Recent Returns": "المرتجعات الأخيرة",
        "Recent Employees": "الموظفين الأخيرين",
        "Customer Transactions": "معاملات العملاء",
        "Supplier Transactions": "معاملات الموردين",
        "Employee Payments": "مدفوعات الموظفين",
        "Expense Transactions": "معاملات المصروفات",
        "Total Services": "إجمالي الخدمات",
        "Total Mobchanges": "إجمالي تغييرات الجوال",
        "Total Returns": "إجمالي المرتجعات",
        "Total Employees": "إجمالي الموظفين",
        "Service Revenue": "إيرادات الخدمات",
        "Mobchange Revenue": "إيرادات تغيير الجوال",
        "Total Revenue": "إجمالي الإيرادات",
        "Recent Sales": "المبيعات الأخيرة",
        "Recent Purchases": "المشتريات الأخيرة",
        "Low Stock Alerts": "تنبيهات المخزون المنخفض",
        "Overall Sales": "المبيعات الإجمالية",
        "Total Customers": "إجمالي العملاء",
        Suppliers: "الموردين",
        Products: "المنتجات",
        "Inventory Value": "قيمة المخزون",
        "Low Stock Alert": "تنبيه المخزون المنخفض",
        "Expired Products": "المنتجات منتهية الصلاحية",
        "Out of Stock": "نفذ المخزون",
        "Disposed Products": "المنتجات المهملة",
        "Discount Products": "منتجات الخصم",
        "Bonus Products": "منتجات المكافأة",
        "Expiring Soon": "تنتهي قريباً",
        Pending: "قيد الانتظار",
        Completed: "مكتمل",
        "In Progress": "قيد التقدم",
        Paid: "مدفوع",
        Unpaid: "غير مدفوع",
        Partial: "جزئي",
        "Last Week": "الأسبوع الماضي",
        "Last Month": "الشهر الماضي",
        "Last 6 Months": "آخر 6 أشهر",
        "Custom Date": "تاريخ مخصص",
        "Filter by Date": "تصفية حسب التاريخ",
        "Advanced Sections": "الأقسام المتقدمة",
        "Date Range": "نطاق التاريخ",
        "From": "من",
        "To": "إلى",
        "Apply Filter": "تطبيق الفلتر",
        "Clear Filter": "مسح الفلتر",
        "Customer Paid": "مدفوعات العملاء",
        "Customers Balances": "أرصدة العملاء",
      },
    };

    if (dict[targetLang] && dict[targetLang][text]) {
      return dict[targetLang][text];
    }

    try {
      const response = await fetch("https://translate.argosopentech.com/translate", {
        method: "POST",
        body: JSON.stringify({ q: text, source: "en", target: targetLang }),
        headers: { "Content-Type": "application/json" },
      });
      if (response.ok) {
        const data = await response.json();
        return data.translatedText;
      }
    } catch (e) {
      console.log("Translation failed");
    }
    return text;
  };

  const translateText = async (text, targetLang) => {
    const cacheKey = `${text}_${targetLang}`;
    if (cache.has(cacheKey)) {
      return cache.get(cacheKey);
    }
    const translated = await translateAPI(text, targetLang);
    cache.set(cacheKey, translated);
    saveToCache();
    return translated;
  };

  const defaultTexts = {
    dashboardTitle: "Analytics Dashboard",
    welcome: "Welcome",
    services: "Services",
    mobchange: "Mobo oil change",
    returns: "Returns",
    employees: "Employees",
    transactions: "Transactions",
    recentServices: "Recent Services",
    recentMobchanges: "Recent Mobo oil changes",
    recentReturns: "Recent Returns",
    recentEmployees: "Recent Employees",
    customerTransactions: "Customer Transactions",
    supplierTransactions: "Supplier Transactions",
    employeePayments: "Employee Payments",
    expenseTransactions: "Expense Transactions",
    totalServices: "Total Services",
    totalMobchanges: "Total Mobo oil changes",
    totalReturns: "Total Returns",
    totalEmployees: "Total Employees",
    serviceRevenue: "Service Revenue",
    mobchangeRevenue: "Mobo oil Revenue",
    recentSales: "Recent Sales",
    recentPurchases: "Recent Purchases",
    lowStockAlerts: "Low Stock Alerts",
    overallSales: "Overall Sales",
    totalCustomers: "Total Customers",
    suppliers: "Total Suppliers",
    products: "Total Products",
    inventoryValue: "Inventory Value",
    lowStockAlert: "Low Stock Alert",
    expiredProducts: "Expired Products",
    outOfStock: "Out of Stock",
    disposedProducts: "Disposed Products",
    discountProducts: "Discount Products",
    bonusProducts: "Bonus Products",
    expiringSoon: "Expiring Soon",
    billNo: "Bill No.",
    customer: "Customer",
    date: "Date",
    amount: "Amount",
    status: "Status",
    serviceNo: "Service No.",
    type: "Type",
    employee: "Employee",
    paid: "Paid",
    unpaid: "Unpaid",
    partial: "Partial",
    pending: "Pending",
    completed: "Completed",
    inProgress: "In Progress",
    quantity: "Quantity",
    price: "Price",
    total: "Total",
    lastWeek: "Last Week",
    lastMonth: "Last Month",
    last6Months: "Last 6 Months",
    customDate: "Custom Date",
    filterByDate: "Filter by Date",
    advancedSections: "Advanced Sections",
    dateRange: "Date Range",
    from: "From",
    to: "To",
    applyFilter: "Apply Filter",
    clearFilter: "Clear Filter",
    customerPaid: "Customer Paid",
    customersBalances: "Customers Balances",
  };

  const getTranslations = async (lang) => {
    if (lang === "en") return defaultTexts;
    const translations = {};
    const promises = [];
    Object.keys(defaultTexts).forEach((key) => {
      const cacheKey = `${defaultTexts[key]}_${lang}`;
      if (cache.has(cacheKey)) {
        translations[key] = cache.get(cacheKey);
      } else {
        promises.push(
          translateText(defaultTexts[key], lang).then((translated) => {
            translations[key] = translated;
          })
        );
      }
    });
    if (promises.length > 0) {
      await Promise.all(promises);
      saveToCache();
    }
    return translations;
  };

  loadFromCache();
  return { getTranslations, translateText };
};

const dashboardTranslator = createDashboardTranslator();

// ==================== SHARED VISUAL HELPERS ====================
const handleSpotlightMove = (e) => {
  const card = e.currentTarget;
  const rect = card.getBoundingClientRect();
  card.style.setProperty("--mx", `${e.clientX - rect.left}px`);
  card.style.setProperty("--my", `${e.clientY - rect.top}px`);
};

const useCountUp = (rawValue, duration = 900) => {
  const [display, setDisplay] = useState(0);
  const prevRef = useRef(0);

  useEffect(() => {
    const end = typeof rawValue === "number" ? rawValue : parseFloat(rawValue) || 0;
    const start = prevRef.current;

    if (!Number.isFinite(end)) {
      setDisplay(0);
      return;
    }
    if (start === end) {
      setDisplay(end);
      return;
    }

    let raf;
    const startTime = performance.now();

    const tick = (now) => {
      const progress = Math.min((now - startTime) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setDisplay(start + (end - start) * eased);
      if (progress < 1) {
        raf = requestAnimationFrame(tick);
      } else {
        prevRef.current = end;
      }
    };

    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [rawValue, duration]);

  return display;
};

// ==================== TOAST NOTIFICATION ====================
const DashboardToast = ({ toast, setToast }) => {
  if (!toast.show) return null;
  return (
    <div className={`ozanalyticsui-toast ${toast.type}`}>
      <div className="ozanalyticsui-toast-accent" />
      <div className="ozanalyticsui-toast-timer">
        <div className="ozanalyticsui-toast-progress-bar" />
      </div>
    </div>
  );
};

// ==================== STAT CARD COMPONENT ====================
const StatCard = ({ 
  title, 
  value, 
  icon: Icon, 
  color, 
  subtitle, 
  onClick,
  clickable = false,
  badge = null,
  badgeColor = "primary"
}) => {
  const currencyCards = [
    "Overall Sales",
    "Inventory Value",
    "Service Revenue",
    "Mobchange Revenue",
    "Customer Paid",
    "Customers Balances",
  ];
  const shouldShowCurrency = currencyCards.includes(title);
  const numericValue = typeof value === "number" ? value : parseFloat(value) || 0;
  const animatedValue = useCountUp(numericValue);

  const getColorClass = () => {
    const colorMap = {
      primary: "ozanalyticsui-stat-primary",
      success: "ozanalyticsui-stat-success",
      info: "ozanalyticsui-stat-info",
      warning: "ozanalyticsui-stat-warning",
      secondary: "ozanalyticsui-stat-secondary",
      purple: "ozanalyticsui-stat-purple",
      danger: "ozanalyticsui-stat-danger",
      teal: "ozanalyticsui-stat-teal",
      orange: "ozanalyticsui-stat-orange",
      pink: "ozanalyticsui-stat-pink",
    };
    return colorMap[color] || "ozanalyticsui-stat-primary";
  };

  const displayValue = shouldShowCurrency
    ? formatPKR(animatedValue)
    : Math.round(animatedValue).toLocaleString("en-PK");

  return (
    <div 
      className={`ozanalyticsui-stat-card ${getColorClass()} ${clickable ? 'ozanalyticsui-stat-clickable' : ''}`} 
      onMouseMove={handleSpotlightMove}
      onClick={clickable ? onClick : undefined}
      style={clickable ? { cursor: 'pointer' } : {}}
    >
      <div className="ozanalyticsui-stat-card-spotlight" />
      <div className="ozanalyticsui-stat-card-content">
        <div className="ozanalyticsui-stat-card-header">
          <h4>{title}</h4>
          <div className="ozanalyticsui-stat-card-icon">
            <Icon />
          </div>
        </div>
        <div className="ozanalyticsui-stat-card-value">{displayValue}</div>
        {subtitle && <div className="ozanalyticsui-stat-card-subtitle">{subtitle}</div>}
        {badge && (
          <div className={`ozanalyticsui-stat-card-badge ozanalyticsui-badge-${badgeColor}`}>
            {badge}
          </div>
        )}
      </div>
      <div className="ozanalyticsui-stat-card-bg-glow"></div>
      <div className="ozanalyticsui-stat-card-accent-bar"></div>
    </div>
  );
};

// ==================== SKELETON LOADER ====================
const DashboardSkeleton = ({ darkMode }) => (
  <div className={`ozanalyticsui-container ${darkMode ? "dark" : "light"}`}>
    <div className="ozanalyticsui-bg-animation">
      <div className="ozanalyticsui-gradient-orb ozanalyticsui-orb-1"></div>
      <div className="ozanalyticsui-gradient-orb ozanalyticsui-orb-2"></div>
      <div className="ozanalyticsui-gradient-orb ozanalyticsui-orb-3"></div>
    </div>
    <div className="ozanalyticsui-skeleton ozanalyticsui-skeleton-header" />
    <div className="ozanalyticsui-stats-grid">
      {Array.from({ length: 16 }).map((_, i) => (
        <div
          key={i}
          className="ozanalyticsui-skeleton ozanalyticsui-skeleton-card"
          style={{ animationDelay: `${i * 0.06}s` }}
        />
      ))}
    </div>
    <div className="ozanalyticsui-charts-row">
      <div className="ozanalyticsui-skeleton ozanalyticsui-skeleton-chart" />
      <div className="ozanalyticsui-skeleton ozanalyticsui-skeleton-chart" />
    </div>
  </div>
);

// ==================== DATE FILTER COMPONENT ====================
const DateFilter = ({ 
  activeFilter, 
  onFilterSelect, 
  customStartDate, 
  customEndDate, 
  onCustomDateChange,
  onApplyCustomDate,
  onClearFilter,
  translations,
  darkMode
}) => {
  const [showDatePicker, setShowDatePicker] = useState(false);

  const filterOptions = [
    { id: 'today', label: 'Today' },
    { id: 'lastweek', label: 'Last Week' },
    { id: 'lastmonth', label: 'Last Month' },
    { id: 'last6months', label: 'Last 6 Months' },
    { id: 'custom', label: 'Custom' },
  ];

  const getFilterLabel = (id) => {
    const map = {
      'today': 'Today',
      'lastweek': 'Last Week',
      'lastmonth': 'Last Month',
      'last6months': 'Last 6 Months',
      'custom': 'Custom',
    };
    return map[id] || id;
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      const wrapper = document.querySelector('.ozanalyticsui-date-filter-wrapper');
      if (wrapper && !wrapper.contains(event.target)) {
        setShowDatePicker(false);
      }
    };

    if (showDatePicker) {
      document.addEventListener('mousedown', handleClickOutside);
      // Prevent body scroll when dropdown is open
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.body.style.overflow = '';
    };
  }, [showDatePicker]);

  return (
    <>
      {/* Backdrop Overlay - Blurs the background */}
      {showDatePicker && (
        <div 
          className="ozanalyticsui-date-filter-overlay"
          onClick={() => setShowDatePicker(false)}
        />
      )}

      <div className="ozanalyticsui-date-filter-wrapper">
        <div className="ozanalyticsui-date-filter-trigger" onClick={() => setShowDatePicker(!showDatePicker)}>
          <FaCalendarAlt className="ozanalyticsui-filter-icon" />
          <span className="ozanalyticsui-filter-label">
            {translations.dateRange || 'Date Range'}: {getFilterLabel(activeFilter)}
            {activeFilter === 'custom' && customStartDate && customEndDate && (
              <span className="ozanalyticsui-filter-dates">
                ({customStartDate} - {customEndDate})
              </span>
            )}
          </span>
          <FaFilter className="ozanalyticsui-filter-chevron" />
        </div>

        {showDatePicker && (
          <div className="ozanalyticsui-date-filter-dropdown">
            <div className="ozanalyticsui-dropdown-accent" />
            
            <div className="ozanalyticsui-filter-presets">
              {filterOptions.map((option) => (
                <button
                  key={option.id}
                  className={`ozanalyticsui-filter-preset-btn ${activeFilter === option.id ? 'active' : ''}`}
                  onClick={() => {
                    onFilterSelect(option.id);
                    if (option.id !== 'custom') {
                      setShowDatePicker(false);
                    }
                  }}
                >
                  {option.label}
                  {activeFilter === option.id && <FaCheckCircle className="ozanalyticsui-filter-check" />}
                </button>
              ))}
            </div>

            {activeFilter === 'custom' && (
              <div className="ozanalyticsui-custom-date-range">
                <div className="ozanalyticsui-date-input-group">
                  <label>{translations.from || 'From'}</label>
                  <input
                    type="date"
                    value={customStartDate}
                    onChange={(e) => onCustomDateChange('start', e.target.value)}
                    className="ozanalyticsui-date-input"
                  />
                </div>
                <div className="ozanalyticsui-date-input-group">
                  <label>{translations.to || 'To'}</label>
                  <input
                    type="date"
                    value={customEndDate}
                    onChange={(e) => onCustomDateChange('end', e.target.value)}
                    className="ozanalyticsui-date-input"
                  />
                </div>
                <div className="ozanalyticsui-filter-actions">
                  <button 
                    className="ozanalyticsui-apply-btn"
                    onClick={() => {
                      onApplyCustomDate();
                      setShowDatePicker(false);
                    }}
                  >
                    {translations.applyFilter || 'Apply Filter'}
                  </button>
                  <button 
                    className="ozanalyticsui-clear-btn"
                    onClick={() => {
                      onClearFilter();
                      setShowDatePicker(false);
                    }}
                  >
                    {translations.clearFilter || 'Clear Filter'}
                  </button>
                </div>
              </div>
            )}

            {activeFilter !== 'custom' && (
              <div className="ozanalyticsui-filter-footer">
                <button 
                  className="ozanalyticsui-clear-btn"
                  onClick={() => {
                    onClearFilter();
                    setShowDatePicker(false);
                  }}
                >
                  {translations.clearFilter || 'Clear Filter'}
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </>
  );
};

// ==================== MAIN DASHBOARD COMPONENT ====================
const PharmacyDashboardHome = ({ 
  onNavigate,
  setFormContent,
  onBreadcrumbUpdate
}) => {
  const { user, currentShop, loading: authLoading } = useAuth();
  const [loading, setLoading] = useState(true);
  const [currentLanguage, setCurrentLanguage] = useState(() => localStorage.getItem("ourzest_language") || "en");
  const [translations, setTranslations] = useState({});
  const [darkMode, setDarkMode] = useState(() => localStorage.getItem("ourzest_darkMode") === "true");
  const [themeColor, setThemeColor] = useState(() => localStorage.getItem("ourzest_theme") || "#6366f1");
  const [toast, setToast] = useState({ show: false, type: "", message: "" });
  const [activeDateFilter, setActiveDateFilter] = useState("lastmonth");
  const [customStartDate, setCustomStartDate] = useState("");
  const [customEndDate, setCustomEndDate] = useState("");

  const [visibleSections, setVisibleSections] = useState({
    recentServices: false,
    recentMobchanges: false,
    recentReturns: false,
    recentEmployees: false,
    customerTransactions: false,
    supplierTransactions: false,
    employeePayments: false,
    expenseTransactions: false,
    lowStockAlerts: false,
  });

  const [stats, setStats] = useState({
    totalSuppliers: 0,
    totalCustomers: 0,
    totalProducts: 0,
    lowStockCount: 0,
    inventoryValue: 0,
    overallSales: 0,
    overallSalesCount: 0,
    monthSales: 0,
    totalPurchases: 0,
    // New fields for customer payments and balances
    customerPaid: 0,
    customersBalances: 0,
  });

  // New product stats state
  const [productStats, setProductStats] = useState({
    expiredCount: 0,
    outOfStockCount: 0,
    disposedCount: 0,
    discountCount: 0,
    bonusCount: 0,
    expiringCount: 0,
    expiringProducts: [],
    expiredProducts: [],
    outOfStockProducts: [],
    disposedProducts: [],
    discountProducts: [],
    bonusProducts: [],
  });

  const [moduleStats, setModuleStats] = useState({
    totalServices: 0,
    pendingServices: 0,
    completedServices: 0,
    inProgressServices: 0,
    totalMobchanges: 0,
    pendingMobchanges: 0,
    completedMobchanges: 0,
    totalReturns: 0,
    totalReturnsAmount: 0,
    totalEmployees: 0,
    activeEmployees: 0,
    totalSalaryPaid: 0,
    serviceRevenue: 0,
    mobchangeRevenue: 0,
  });

  const [salesTrendData, setSalesTrendData] = useState([]);
  const [recentSales, setRecentSales] = useState([]);
  const [recentPurchases, setRecentPurchases] = useState([]);
  const [recentServices, setRecentServices] = useState([]);
  const [recentMobchanges, setRecentMobchanges] = useState([]);
  const [recentReturns, setRecentReturns] = useState([]);
  const [recentEmployees, setRecentEmployees] = useState([]);
  const [customerTransactions, setCustomerTransactions] = useState([]);
  const [supplierTransactions, setSupplierTransactions] = useState([]);
  const [employeePayments, setEmployeePayments] = useState([]);
  const [expenseTransactions, setExpenseTransactions] = useState([]);
  const [lowStockItems, setLowStockItems] = useState([]);

  const [filters, setFilters] = useState({ startDate: "", endDate: "", category: "all", paymentStatus: "all" });

  // ============================================
  // NAVIGATION HANDLERS
  // ============================================
  const navigateToMenu = useCallback((menuName, submenuName = null) => {
    if (onNavigate) {
      onNavigate(menuName, submenuName);
    }
  }, [onNavigate]);

  const openList = useCallback((listType) => {
    const navigationMap = {
      "Inventory Value": { menu: 'stock', submenu: 'stocklist' },
      "Overall Sales": { menu: 'sales', submenu: 'saleslist' },
      "Customer Paid": { menu: 'sales', submenu: 'saleslist' },
      "Customers Balances": { menu: 'customers', submenu: 'customerlist' },
      "Total Customers": { menu: 'customers', submenu: 'customerlist' },
      "Suppliers": { menu: 'suppliers', submenu: 'supplierlist' },
      "Total Services": { menu: 'services', submenu: 'serviceshistory' },
      "Total Mobchanges": { menu: 'mobchange', submenu: 'mobchangelist' },
      "Total Returns": { menu: 'returns', submenu: 'returnlist' },
      "Total Employees": { menu: 'employees', submenu: 'employeeslist' },
      "Products": { menu: 'stock', submenu: 'stocklist' },
      "Low Stock Alert": { menu: 'stock', submenu: 'lowstock' },
      "Recent Sales": { menu: 'sales', submenu: 'saleslist' },
      "Recent Purchases": { menu: 'purchases', submenu: 'purchaselist' },
      "Service Revenue": { menu: 'services', submenu: 'serviceshistory' },
      "Mobchange Revenue": { menu: 'mobchange', submenu: 'mobchangelist' },
      "Expired Products": { menu: 'stock', submenu: 'expiredproducts', filter: 'expired' },
      "Out of Stock": { menu: 'stock', submenu: 'outofstock', filter: 'outofstock' },
      "Disposed Products": { menu: 'stock', submenu: 'disposedstock', filter: 'disposed' },
      "Discount Products": { menu: 'stock', submenu: 'discounts', filter: 'discount' },
      "Bonus Products": { menu: 'stock', submenu: 'bonusproducts', filter: 'bonus' },
      "Expiring Soon": { menu: 'stock', submenu: 'expiringproducts', filter: 'expiring' },
    };

    const nav = navigationMap[listType];
    if (nav) {
      navigateToMenu(nav.menu, nav.submenu);
    }
  }, [navigateToMenu]);

  // Helper function to get date range based on filter
  const getDateRangeFromFilter = useCallback((filter) => {
    const today = new Date();
    let startDate = "";
    let endDate = today.toISOString().split("T")[0];

    switch (filter) {
      case "today":
        startDate = endDate;
        break;
      case "lastweek":
        const weekAgo = new Date(today);
        weekAgo.setDate(today.getDate() - 7);
        startDate = weekAgo.toISOString().split("T")[0];
        break;
      case "lastmonth":
        const monthAgo = new Date(today);
        monthAgo.setMonth(today.getMonth() - 1);
        startDate = monthAgo.toISOString().split("T")[0];
        break;
      case "last6months":
        const sixMonthsAgo = new Date(today);
        sixMonthsAgo.setMonth(today.getMonth() - 6);
        startDate = sixMonthsAgo.toISOString().split("T")[0];
        break;
      case "custom":
        startDate = customStartDate;
        endDate = customEndDate;
        break;
      default:
        const defaultMonthAgo = new Date(today);
        defaultMonthAgo.setMonth(today.getMonth() - 1);
        startDate = defaultMonthAgo.toISOString().split("T")[0];
    }
    return { startDate, endDate };
  }, [customStartDate, customEndDate]);

  // ============================================
  // THEME COLOR AUTO-SYNC
  // ============================================
  const applyTheme = useCallback((color) => {
    if (!color || !/^#[0-9A-F]{6}$/i.test(color)) {
      color = "#6366f1";
    }
    document.documentElement.style.setProperty("--ozanalyticsui-primary-color", color);
    document.documentElement.style.setProperty("--ozanalyticsui-primary-dark", color);
    document.documentElement.style.setProperty("--ozanalyticsui-primary-light", color);

    let r, g, b;
    if (color.startsWith("#")) {
      r = parseInt(color.slice(1, 3), 16);
      g = parseInt(color.slice(3, 5), 16);
      b = parseInt(color.slice(5, 7), 16);
    } else {
      r = 99; g = 102; b = 241;
    }

    let style = document.getElementById("dynamic-orb-styles");
    if (!style) {
      style = document.createElement("style");
      style.id = "dynamic-orb-styles";
      document.head.appendChild(style);
    }

    style.textContent = `
      .ozanalyticsui-bg-animation .ozanalyticsui-gradient-orb { opacity: 0.4; }
      .ozanalyticsui-bg-animation .ozanalyticsui-gradient-orb.ozanalyticsui-orb-1 { background: radial-gradient(circle, rgba(${r}, ${g}, ${b}, 0.4) 0%, rgba(${r}, ${g}, ${b}, 0.05) 100%); }
      .ozanalyticsui-bg-animation .ozanalyticsui-gradient-orb.ozanalyticsui-orb-2 { background: radial-gradient(circle, rgba(${r}, ${g}, ${b}, 0.35) 0%, rgba(${r}, ${g}, ${b}, 0.04) 100%); }
      .ozanalyticsui-bg-animation .ozanalyticsui-gradient-orb.ozanalyticsui-orb-3 { background: radial-gradient(circle, rgba(${r}, ${g}, ${b}, 0.3) 0%, rgba(${r}, ${g}, ${b}, 0.03) 100%); }
      .ozanalyticsui-bg-animation .ozanalyticsui-gradient-orb.ozanalyticsui-orb-4 { background: radial-gradient(circle, rgba(${r}, ${g}, ${b}, 0.25) 0%, rgba(${r}, ${g}, ${b}, 0.02) 100%); }
      .ozanalyticsui-bg-animation .ozanalyticsui-gradient-orb.ozanalyticsui-orb-5 { background: radial-gradient(circle, rgba(${r}, ${g}, ${b}, 0.2) 0%, rgba(${r}, ${g}, ${b}, 0.01) 100%); }
      
      .ozanalyticsui-stat-primary .ozanalyticsui-stat-card-icon { color: ${color} !important; }
      .ozanalyticsui-stat-primary .ozanalyticsui-stat-card-accent-bar { background: ${color} !important; }
      
      .ozanalyticsui-view-all-btn { color: ${color} !important; }
      .ozanalyticsui-view-all-btn:hover { background: ${color}15 !important; }
    `;
  }, []);

  // ============================================
  // DARK MODE AUTO-SYNC
  // ============================================
  const applyDarkMode = useCallback((isDark) => {
    const container = document.querySelector(".ozanalyticsui-container");
    if (container) {
      if (isDark) {
        container.classList.add("dark");
        container.classList.remove("light");
      } else {
        container.classList.add("light");
        container.classList.remove("dark");
      }
    }

    if (isDark) {
      document.body.classList.add("dashboard-dark");
      document.documentElement.classList.add("dashboard-dark");
    } else {
      document.body.classList.remove("dashboard-dark");
      document.documentElement.classList.remove("dashboard-dark");
    }
  }, []);

  // ============================================
  // LANGUAGE AUTO-SYNC WITH RTL
  // ============================================
  const loadTranslations = useCallback(async (lang) => {
    const trans = await dashboardTranslator.getTranslations(lang);
    setTranslations(trans);
  }, []);

  const applyRTL = useCallback((lang) => {
    const isRTL = lang === "ur" || lang === "ar";
    const container = document.querySelector(".ozanalyticsui-container");
    if (container) {
      if (isRTL) {
        container.classList.add("rtl");
        container.classList.remove("ltr");
      } else {
        container.classList.add("ltr");
        container.classList.remove("rtl");
      }
    }

    if (isRTL) {
      document.documentElement.dir = "rtl";
      document.body.dir = "rtl";
    } else {
      document.documentElement.dir = "ltr";
      document.body.dir = "ltr";
    }
  }, []);

  // ============================================
  // INITIAL SETUP AND SYNC
  // ============================================
  useEffect(() => {
    const savedTheme = localStorage.getItem("ourzest_theme") || "#6366f1";
    const savedDarkMode = localStorage.getItem("ourzest_darkMode") === "true";
    const savedLanguage = localStorage.getItem("ourzest_language") || "en";

    setThemeColor(savedTheme);
    setDarkMode(savedDarkMode);
    setCurrentLanguage(savedLanguage);

    applyTheme(savedTheme);
    applyDarkMode(savedDarkMode);
    loadTranslations(savedLanguage);
    applyRTL(savedLanguage);
  }, []);

  // ============================================
  // LISTEN FOR EXTERNAL CHANGES
  // ============================================
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === "ourzest_language") {
        const newLang = e.newValue || "en";
        if (newLang !== currentLanguage) {
          setCurrentLanguage(newLang);
          loadTranslations(newLang);
          applyRTL(newLang);
        }
      }
      if (e.key === "ourzest_darkMode") {
        const isDark = e.newValue === "true";
        if (isDark !== darkMode) {
          setDarkMode(isDark);
          applyDarkMode(isDark);
        }
      }
      if (e.key === "ourzest_theme") {
        const newTheme = e.newValue || "#6366f1";
        if (newTheme !== themeColor) {
          setThemeColor(newTheme);
          applyTheme(newTheme);
        }
      }
    };

    const handleLanguageChanged = (event) => {
      if (event.detail && event.detail.language) {
        const newLang = event.detail.language;
        if (newLang !== currentLanguage) {
          setCurrentLanguage(newLang);
          loadTranslations(newLang);
          applyRTL(newLang);
        }
      }
    };

    const handleDarkModeToggled = (event) => {
      if (event.detail && event.detail.darkMode !== undefined) {
        const isDark = event.detail.darkMode;
        if (isDark !== darkMode) {
          setDarkMode(isDark);
          applyDarkMode(isDark);
        }
      }
    };

    const handleThemeChanged = (event) => {
      if (event.detail && event.detail.theme) {
        const newTheme = event.detail.theme;
        if (newTheme !== themeColor) {
          setThemeColor(newTheme);
          applyTheme(newTheme);
        }
      }
    };

    // Poll for changes
    const interval = setInterval(() => {
      const storedLanguage = localStorage.getItem("ourzest_language") || "en";
      const storedDarkMode = localStorage.getItem("ourzest_darkMode") === "true";
      const storedTheme = localStorage.getItem("ourzest_theme") || "#6366f1";

      if (storedLanguage !== currentLanguage) {
        setCurrentLanguage(storedLanguage);
        loadTranslations(storedLanguage);
        applyRTL(storedLanguage);
      }

      if (storedDarkMode !== darkMode) {
        setDarkMode(storedDarkMode);
        applyDarkMode(storedDarkMode);
      }

      if (storedTheme !== themeColor) {
        setThemeColor(storedTheme);
        applyTheme(storedTheme);
      }
    }, 1000);

    window.addEventListener("storage", handleStorageChange);
    window.addEventListener("languageChanged", handleLanguageChanged);
    window.addEventListener("darkModeToggled", handleDarkModeToggled);
    window.addEventListener("themeChanged", handleThemeChanged);

    return () => {
      window.removeEventListener("storage", handleStorageChange);
      window.removeEventListener("languageChanged", handleLanguageChanged);
      window.removeEventListener("darkModeToggled", handleDarkModeToggled);
      window.removeEventListener("themeChanged", handleThemeChanged);
      clearInterval(interval);
    };
  }, [currentLanguage, darkMode, themeColor, applyDarkMode, applyTheme, loadTranslations, applyRTL]);

  const showToast = (type, message) => {
    setToast({ show: true, type, message });
    setTimeout(() => setToast({ show: false }), 3000);
  };

  const loadDashboardData = useCallback(async () => {
    if (!user || !currentShop) {
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const shopId = currentShop?.shop_id;

      const { startDate, endDate } = getDateRangeFromFilter(activeDateFilter);

      if (activeDateFilter === 'custom' && (!customStartDate || !customEndDate)) {
        setLoading(false);
        return;
      }

      const updatedFilters = {
        startDate,
        endDate,
        category: filters.category,
        paymentStatus: filters.paymentStatus
      };

      setAuthData(user, currentShop);

      const [dashboardData, servicesData, mobchangeData, returnsData, employeesData, transactionsData, purchasesData, productStatsData] =
        await Promise.all([
          fetchDashboardData(updatedFilters),
          fetchServicesData(shopId, user.username, updatedFilters),
          fetchMobchangeData(shopId, user.username, updatedFilters),
          fetchReturnsData(shopId, user.username, updatedFilters),
          fetchEmployeesData(shopId, user.username),
          fetchTransactionsData(shopId, updatedFilters),
          fetchPurchasesData(shopId, user.username, updatedFilters),
          fetchProductStats(shopId),
        ]);

      if (dashboardData && dashboardData.stats) {
        setStats({
          totalSuppliers: dashboardData.stats?.total_suppliers || 0,
          totalCustomers: dashboardData.stats?.total_customers || 0,
          totalProducts: dashboardData.stats?.total_products || 0,
          lowStockCount: dashboardData.stats?.low_stock_count || 0,
          inventoryValue: dashboardData.stats?.inventory_value || 0,
          overallSales: dashboardData.stats?.overall_sales || 0,
          overallSalesCount: dashboardData.stats?.overall_sales_count || 0,
          monthSales: dashboardData.stats?.month_sales || 0,
          totalPurchases: dashboardData.stats?.total_purchases || 0,
          customerPaid: dashboardData.stats?.customer_paid || 0,
          customersBalances: dashboardData.stats?.customers_balances || 0,
        });

        const labels = dashboardData.trend?.labels || [];
        const values = dashboardData.trend?.values || [];
        
        const formattedTrendData = labels.map((label, index) => ({
          name: label,
          revenue: values[index] || 0,
        }));

        setSalesTrendData(formattedTrendData);
        setRecentSales(dashboardData.recent_sales || []);
        setLowStockItems(dashboardData.low_stock || []);
      }

      // Set product stats from the new API call
      if (productStatsData) {
        setProductStats({
          expiredCount: productStatsData.expired_count || 0,
          outOfStockCount: productStatsData.out_of_stock_count || 0,
          disposedCount: productStatsData.disposed_count || 0,
          discountCount: productStatsData.discount_count || 0,
          bonusCount: productStatsData.bonus_count || 0,
          expiringCount: productStatsData.expiring_count || 0,
          expiringProducts: productStatsData.expiring_products || [],
          expiredProducts: productStatsData.expired_products || [],
          outOfStockProducts: productStatsData.out_of_stock_products || [],
          disposedProducts: productStatsData.disposed_products || [],
          discountProducts: productStatsData.discount_products || [],
          bonusProducts: productStatsData.bonus_products || [],
        });
      }

      if (servicesData) {
        setModuleStats(prev => ({
          ...prev,
          totalServices: servicesData.total || 0,
          pendingServices: servicesData.pending || 0,
          completedServices: servicesData.completed || 0,
          inProgressServices: servicesData.in_progress || 0,
          serviceRevenue: servicesData.total_revenue || 0,
        }));
        setRecentServices(servicesData.recent || []);
      }

      if (mobchangeData) {
        setModuleStats(prev => ({
          ...prev,
          totalMobchanges: mobchangeData.total || 0,
          pendingMobchanges: mobchangeData.pending || 0,
          completedMobchanges: mobchangeData.completed || 0,
          mobchangeRevenue: mobchangeData.total_revenue || 0,
        }));
        setRecentMobchanges(mobchangeData.recent || []);
      }

      if (returnsData) {
        setModuleStats(prev => ({
          ...prev,
          totalReturns: returnsData.total || 0,
          totalReturnsAmount: returnsData.total_amount || 0,
        }));
        setRecentReturns(returnsData.recent || []);
      }

      if (employeesData) {
        setModuleStats(prev => ({
          ...prev,
          totalEmployees: employeesData.total || 0,
          activeEmployees: employeesData.active || 0,
          totalSalaryPaid: employeesData.total_salary_paid || 0,
        }));
        setRecentEmployees(employeesData.recent || []);
      }

      if (transactionsData) {
        setCustomerTransactions(transactionsData.customer || []);
        setSupplierTransactions(transactionsData.supplier || []);
        setEmployeePayments(transactionsData.employee || []);
        setExpenseTransactions(transactionsData.expense || []);
      }

      if (purchasesData) {
        setRecentPurchases(purchasesData.recent || []);
      }

      const filterName = activeDateFilter === "today" ? "Today" :
                         activeDateFilter === "lastweek" ? "Last Week" :
                         activeDateFilter === "lastmonth" ? "Last Month" :
                         activeDateFilter === "last6months" ? "Last 6 Months" : "Custom Range";

      showToast("success", `Dashboard updated for ${filterName}`);
    } catch (error) {
      console.error("Error loading dashboard:", error);
      showToast("error", error.message || "Failed to load dashboard data");
    } finally {
      setLoading(false);
    }
  }, [user, currentShop, filters, activeDateFilter, customStartDate, customEndDate, getDateRangeFromFilter]);

  useEffect(() => {
    if (!authLoading && user && currentShop) {
      loadDashboardData();
    }
  }, [loadDashboardData, authLoading, user, currentShop]);

  useEffect(() => {
    if (user && currentShop && !authLoading && !loading) {
      loadDashboardData();
    }
  }, [activeDateFilter, customStartDate, customEndDate]);

  const handleFilterSelect = (filterId) => {
    setActiveDateFilter(filterId);
    if (filterId !== "custom") {
      setCustomStartDate("");
      setCustomEndDate("");
    }
  };

  const handleCustomDateChange = (type, value) => {
    if (type === "start") {
      setCustomStartDate(value);
    } else {
      setCustomEndDate(value);
    }
  };

  const handleApplyCustomDate = () => {
    if (customStartDate && customEndDate) {
      loadDashboardData();
    } else {
      showToast("warning", "Please select both start and end dates");
    }
  };

  const handleClearFilter = () => {
    setActiveDateFilter("lastmonth");
    setCustomStartDate("");
    setCustomEndDate("");
    loadDashboardData();
  };

  const toggleSection = (sectionId) => {
    setVisibleSections(prev => ({ ...prev, [sectionId]: !prev[sectionId] }));
  };

  if (authLoading || loading) {
    return <DashboardSkeleton darkMode={darkMode} />;
  }

  return (
    <div className={`ozanalyticsui-container ${darkMode ? "dark" : "light"} ${currentLanguage === "ur" || currentLanguage === "ar" ? "rtl" : "ltr"}`}>
      <DashboardToast toast={toast} setToast={setToast} />

      {/* Animated Background */}
      <div className="ozanalyticsui-bg-animation">
        <div className="ozanalyticsui-grain-overlay"></div>
        <div className="ozanalyticsui-gradient-orb ozanalyticsui-orb-1"></div>
        <div className="ozanalyticsui-gradient-orb ozanalyticsui-orb-2"></div>
        <div className="ozanalyticsui-gradient-orb ozanalyticsui-orb-3"></div>
        <div className="ozanalyticsui-gradient-orb ozanalyticsui-orb-4"></div>
        <div className="ozanalyticsui-gradient-orb ozanalyticsui-orb-5"></div>
      </div>

      {/* Header with Date Filter */}
      <div className="ozanalyticsui-header">
        <div className="ozanalyticsui-title-section">
          <div className="ozanalyticsui-title-text">
            <h1>{translations.dashboardTitle || "Analytics Dashboard"}</h1>
          </div>
        </div>
        <div className="ozanalyticsui-header-actions">
          <DateFilter
            activeFilter={activeDateFilter}
            onFilterSelect={handleFilterSelect}
            customStartDate={customStartDate}
            customEndDate={customEndDate}
            onCustomDateChange={handleCustomDateChange}
            onApplyCustomDate={handleApplyCustomDate}
            onClearFilter={handleClearFilter}
            translations={translations}
            darkMode={darkMode}
          />
        </div>
      </div>

      {/* Stats Grid - All Cards Clickable */}
      <div className="ozanalyticsui-stats-grid">
        {/* Overall Sales - Renamed from Today's Sales */}
        <StatCard
          title={translations.inventoryValue || "Inventory Value"}
          value={stats.inventoryValue}
          icon={MdInventory}
          color="purple"
          clickable={true}
          onClick={() => openList("Inventory Value")}
        />
        <StatCard
          title={translations.overallSales || "Overall Sales"}
          value={stats.overallSales}
          icon={FaShoppingCart}
          color="primary"
          subtitle={`${stats.overallSalesCount} invoices`}
          clickable={true}
          onClick={() => openList("Overall Sales")}
        />
        
        {/* ====== NEW: Customer Paid Card ====== */}
        <StatCard
          title={translations.customerPaid || "Customer Paid"}
          value={stats.customerPaid}
          icon={FaWallet}
          color="success"
          subtitle="Total payments received"
          clickable={true}
          onClick={() => openList("Customer Paid")}
        />
        
        {/* ====== NEW: Customers Balances Card ====== */}
         
        <StatCard
          title={translations.customersBalances || "Customers Balances"}
          value={stats.customersBalances}
          icon={FaCreditCard}
          color="warning"
          subtitle="Outstanding balances"
          clickable={true}
          onClick={() => openList("Customers Balances")}
        />
        
        <StatCard
          title={translations.outOfStock || "Out of Stock"}
          value={productStats.outOfStockCount}
          icon={FiPackage}
          color="warning"
          badge={`${productStats.outOfStockCount} items`}
          badgeColor="warning"
          clickable={true}
          onClick={() => openList("Out of Stock")}
        />

 <StatCard
          title={translations.lowStockAlert || "Low Stock Alert"}
          value={stats.lowStockCount}
          icon={FiAlertCircle}
          color="danger"
          subtitle="Need reorder"
          clickable={true}
          onClick={() => openList("Low Stock Alert")}
        />

        {/* NEW PRODUCT STATS CARDS */}
        <StatCard
          title={translations.expiredProducts || "Expired Products"}
          value={productStats.expiredCount}
          icon={FaTimes}
          color="danger"
          badge={`${productStats.expiredCount} items`}
          badgeColor="danger"
          clickable={true}
          onClick={() => openList("Expired Products")}
        />
 
        <StatCard
          title={translations.expiringSoon || "Expiring Soon"}
          value={productStats.expiringCount}
          icon={FiClock}
          color="orange"
          badge={`${productStats.expiringCount} items`}
          badgeColor="warning"
          clickable={true}
          onClick={() => openList("Expiring Soon")}
        />

        <StatCard
          title={translations.products || "Total Products"}
          value={stats.totalProducts}
          icon={FaBoxes}
          color="secondary"
          subtitle={`Total: ${stats.totalProducts}`}
          clickable={true}
          onClick={() => openList("Products")}
        />
       
       
        
        <StatCard
          title={translations.disposedProducts || "Disposed Products"}
          value={productStats.disposedCount}
          icon={FiTrash2}
          color="secondary"
          badge={`${productStats.disposedCount} items`}
          badgeColor="secondary"
          clickable={true}
          onClick={() => openList("Disposed Products")}
        />
        
        <StatCard
          title={translations.discountProducts || "Discount Products"}
          value={productStats.discountCount}
          icon={FiPercent}
          color="info"
          badge={`${productStats.discountCount} items`}
          badgeColor="info"
          clickable={true}
          onClick={() => openList("Discount Products")}
        />
        
        <StatCard
          title={translations.bonusProducts || "Bonus Products"}
          value={productStats.bonusCount}
          icon={FaGift}
          color="success"
          badge={`${productStats.bonusCount} items`}
          badgeColor="success"
          clickable={true}
          onClick={() => openList("Bonus Products")}
        />

          <StatCard
          title={translations.totalReturns || "Total Returns"}
          value={moduleStats.totalReturns}
          icon={FaArrowCircleLeft}
          color="danger"
          subtitle={`Amount: ${formatPKR(moduleStats.totalReturnsAmount)}`}
          clickable={true}
          onClick={() => openList("Total Returns")}
        />

        <StatCard
          title={translations.totalCustomers || "Total Customers"}
          value={stats.totalCustomers}
          icon={FaUsers}
          color="info"
          clickable={true}
          onClick={() => openList("Total Customers")}
        />
        <StatCard
          title={translations.suppliers || "Suppliers"}
          value={stats.totalSuppliers}
          icon={FaTruck}
          color="warning"
          clickable={true}
          onClick={() => openList("Suppliers")}
        />
        
      
        <StatCard
          title={translations.totalEmployees || "Total Employees"}
          value={moduleStats.totalEmployees}
          icon={FaUserCog}
          color="teal"
          subtitle={`Active: ${moduleStats.activeEmployees}`}
          clickable={true}
          onClick={() => openList("Total Employees")}
        />
       
      </div>

      {/* Dynamic Advanced Sections */}
      {visibleSections.recentServices && recentServices.length > 0 && (
        <div className="ozanalyticsui-table-card" onMouseMove={handleSpotlightMove}>
          <div className="ozanalyticsui-card-spotlight" />
          <div className="ozanalyticsui-table-header">
            <h3><FaWrench /> Recent Services</h3>
            <span className="ozanalyticsui-badge">{recentServices.length} records</span>
            <button 
              className="ozanalyticsui-view-all-btn"
              onClick={() => openList("Total Services")}
            >
              View All
            </button>
          </div>
          <div className="ozanalyticsui-table-wrapper">
            <table className="ozanalyticsui-table">
              <thead>
                <tr><th>Service No.</th><th>Customer</th><th>Date</th><th>Amount</th><th>Status</th></tr>
              </thead>
              <tbody>
                {recentServices.slice(0, 5).map((service) => (
                  <tr key={service.id} className="ozanalyticsui-clickable-row" onClick={() => openList("Total Services")} style={{ cursor: 'pointer' }}>
                    <td><strong>{service.service_number}</strong></td>
                    <td>{service.customer_name || "Walk-in"}</td>
                    <td>{service.date}</td>
                    <td className="ozanalyticsui-amount-cell">{formatPKR(service.grand_total)}</td>
                    <td><span className={`ozanalyticsui-status-badge ozanalyticsui-status-${service.status}`}>
                      {service.status === "pending" ? "Pending" : service.status === "in_progress" ? "In Progress" : "Completed"}
                    </span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {visibleSections.recentMobchanges && recentMobchanges.length > 0 && (
        <div className="ozanalyticsui-table-card" onMouseMove={handleSpotlightMove}>
          <div className="ozanalyticsui-card-spotlight" />
          <div className="ozanalyticsui-table-header">
            <h3><FaExchangeAlt /> Recent Mobchanges</h3>
            <span className="ozanalyticsui-badge">{recentMobchanges.length} records</span>
            <button 
              className="ozanalyticsui-view-all-btn"
              onClick={() => openList("Total Mobchanges")}
            >
              View All
            </button>
          </div>
          <div className="ozanalyticsui-table-wrapper">
            <table className="ozanalyticsui-table">
              <thead>
                <tr><th>Mobchange No.</th><th>Customer</th><th>Date</th><th>Amount</th><th>Status</th></tr>
              </thead>
              <tbody>
                {recentMobchanges.slice(0, 5).map((mob) => (
                  <tr key={mob.id} className="ozanalyticsui-clickable-row" onClick={() => openList("Total Mobchanges")} style={{ cursor: 'pointer' }}>
                    <td><strong>{mob.mobchange_number}</strong></td>
                    <td>{mob.customer_name || "Walk-in"}</td>
                    <td>{mob.date}</td>
                    <td className="ozanalyticsui-amount-cell">{formatPKR(mob.grand_total)}</td>
                    <td><span className={`ozanalyticsui-status-badge ozanalyticsui-status-${mob.status}`}>
                      {mob.status === "pending" ? "Pending" : mob.status === "in_progress" ? "In Progress" : "Completed"}
                    </span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {visibleSections.recentReturns && recentReturns.length > 0 && (
        <div className="ozanalyticsui-table-card" onMouseMove={handleSpotlightMove}>
          <div className="ozanalyticsui-card-spotlight" />
          <div className="ozanalyticsui-table-header">
            <h3><FaArrowCircleLeft /> Recent Returns</h3>
            <span className="ozanalyticsui-badge ozanalyticsui-warning-badge">{recentReturns.length} records</span>
            <button 
              className="ozanalyticsui-view-all-btn"
              onClick={() => openList("Total Returns")}
            >
              View All
            </button>
          </div>
          <div className="ozanalyticsui-table-wrapper">
            <table className="ozanalyticsui-table">
              <thead>
                <tr><th>Return No.</th><th>Customer</th><th>Date</th><th>Refund Amount</th><th>Status</th></tr>
              </thead>
              <tbody>
                {recentReturns.slice(0, 5).map((ret) => (
                  <tr key={ret.id} className="ozanalyticsui-clickable-row" onClick={() => openList("Total Returns")} style={{ cursor: 'pointer' }}>
                    <td><strong>{ret.return_number}</strong></td>
                    <td>{ret.customer_name || "Walk-in"}</td>
                    <td>{ret.date}</td>
                    <td className="ozanalyticsui-amount-cell">{formatPKR(ret.refund_amount || ret.grand_total || 0)}</td>
                    <td><span className="ozanalyticsui-status-badge ozanalyticsui-status-completed">Processed</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {visibleSections.recentEmployees && recentEmployees.length > 0 && (
        <div className="ozanalyticsui-table-card" onMouseMove={handleSpotlightMove}>
          <div className="ozanalyticsui-card-spotlight" />
          <div className="ozanalyticsui-table-header">
            <h3><FaUserCog /> Recent Employees</h3>
            <span className="ozanalyticsui-badge">{recentEmployees.length} records</span>
            <button 
              className="ozanalyticsui-view-all-btn"
              onClick={() => openList("Total Employees")}
            >
              View All
            </button>
          </div>
          <div className="ozanalyticsui-table-wrapper">
            <table className="ozanalyticsui-table">
              <thead>
                <tr><th>Employee Name</th><th>Position</th><th>Salary Type</th><th>Status</th><th>Hire Date</th></tr>
              </thead>
              <tbody>
                {recentEmployees.slice(0, 5).map((emp) => (
                  <tr key={emp.employee_id} className="ozanalyticsui-clickable-row" onClick={() => openList("Total Employees")} style={{ cursor: 'pointer' }}>
                    <td>{emp.employee_name}</td>
                    <td>{emp.position || "-"}</td>
                    <td>{emp.salary_type || "fixed"}</td>
                    <td><span className={`ozanalyticsui-status-badge ozanalyticsui-status-${emp.status}`}>{emp.status}</span></td>
                    <td>{emp.hire_date}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {visibleSections.customerTransactions && customerTransactions.length > 0 && (
        <div className="ozanalyticsui-table-card" onMouseMove={handleSpotlightMove}>
          <div className="ozanalyticsui-card-spotlight" />
          <div className="ozanalyticsui-table-header">
            <h3><FaUsers /> Customer Transactions</h3>
            <span className="ozanalyticsui-badge">{customerTransactions.length} records</span>
          </div>
          <div className="ozanalyticsui-table-wrapper">
            <table className="ozanalyticsui-table">
              <thead>
                <tr><th>Date</th><th>Customer</th><th>Type</th><th>Amount</th><th>Paid</th><th>Due</th><th>Status</th></tr>
              </thead>
              <tbody>
                {customerTransactions.slice(0, 8).map((trans) => (
                  <tr key={trans.transaction_id}>
                    <td>{trans.transaction_date}</td>
                    <td>{trans.reference_name || "Customer"}</td>
                    <td>{trans.transaction_type}</td>
                    <td className="ozanalyticsui-amount-cell">{formatPKR(trans.amount)}</td>
                    <td>{formatPKR(trans.amount_paid)}</td>
                    <td className={trans.amount_due > 0 ? "ozanalyticsui-stock-low" : ""}>{formatPKR(trans.amount_due)}</td>
                    <td><span className={`ozanalyticsui-status-badge ozanalyticsui-status-${trans.status}`}>{trans.status}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {visibleSections.supplierTransactions && supplierTransactions.length > 0 && (
        <div className="ozanalyticsui-table-card" onMouseMove={handleSpotlightMove}>
          <div className="ozanalyticsui-card-spotlight" />
          <div className="ozanalyticsui-table-header">
            <h3><FaTruck /> Supplier Transactions</h3>
            <span className="ozanalyticsui-badge">{supplierTransactions.length} records</span>
          </div>
          <div className="ozanalyticsui-table-wrapper">
            <table className="ozanalyticsui-table">
              <thead>
                <tr><th>Date</th><th>Supplier</th><th>Type</th><th>Amount</th><th>Status</th></tr>
              </thead>
              <tbody>
                {supplierTransactions.slice(0, 8).map((trans) => (
                  <tr key={trans.transaction_id}>
                    <td>{trans.transaction_date}</td>
                    <td>{trans.reference_name || "Supplier"}</td>
                    <td>{trans.transaction_type}</td>
                    <td className="ozanalyticsui-amount-cell">{formatPKR(trans.amount)}</td>
                    <td><span className={`ozanalyticsui-status-badge ozanalyticsui-status-${trans.status}`}>{trans.status}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {visibleSections.employeePayments && employeePayments.length > 0 && (
        <div className="ozanalyticsui-table-card" onMouseMove={handleSpotlightMove}>
          <div className="ozanalyticsui-card-spotlight" />
          <div className="ozanalyticsui-table-header">
            <h3><FaMoneyBillWave /> Employee Payments</h3>
            <span className="ozanalyticsui-badge">{employeePayments.length} records</span>
          </div>
          <div className="ozanalyticsui-table-wrapper">
            <table className="ozanalyticsui-table">
              <thead>
                <tr><th>Date</th><th>Employee</th><th>Type</th><th>Amount</th><th>Status</th></tr>
              </thead>
              <tbody>
                {employeePayments.slice(0, 8).map((payment) => (
                  <tr key={payment.payment_id}>
                    <td>{payment.payment_date}</td>
                    <td>{payment.employee_name}</td>
                    <td>{payment.payment_type}</td>
                    <td className="ozanalyticsui-amount-cell">{formatPKR(payment.amount)}</td>
                    <td><span className="ozanalyticsui-status-badge ozanalyticsui-status-paid">Paid</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {visibleSections.expenseTransactions && expenseTransactions.length > 0 && (
        <div className="ozanalyticsui-table-card" onMouseMove={handleSpotlightMove}>
          <div className="ozanalyticsui-card-spotlight" />
          <div className="ozanalyticsui-table-header">
            <h3><MdCategory /> Expense Transactions</h3>
            <span className="ozanalyticsui-badge ozanalyticsui-warning-badge">{expenseTransactions.length} records</span>
          </div>
          <div className="ozanalyticsui-table-wrapper">
            <table className="ozanalyticsui-table">
              <thead>
                <tr><th>Date</th><th>Expense Name</th><th>Category</th><th>Amount</th><th>Status</th></tr>
              </thead>
              <tbody>
                {expenseTransactions.slice(0, 8).map((exp) => (
                  <tr key={exp.expense_id}>
                    <td>{exp.expense_date}</td>
                    <td>{exp.expense_name}</td>
                    <td>{exp.expense_category}</td>
                    <td className="ozanalyticsui-amount-cell">{formatPKR(exp.amount)}</td>
                    <td><span className="ozanalyticsui-status-badge ozanalyticsui-status-paid">{exp.status}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Low Stock Alerts Section */}
      {visibleSections.lowStockAlerts && lowStockItems.length > 0 && (
        <div className="ozanalyticsui-table-card" onMouseMove={handleSpotlightMove}>
          <div className="ozanalyticsui-card-spotlight" />
          <div className="ozanalyticsui-table-header">
            <h3><MdInventory /> Low Stock Alerts</h3>
            <span className="ozanalyticsui-badge ozanalyticsui-warning-badge">{lowStockItems.length} items</span>
            <button 
              className="ozanalyticsui-view-all-btn"
              onClick={() => openList("Low Stock Alert")}
            >
              View All
            </button>
          </div>
          <div className="ozanalyticsui-table-wrapper">
            <table className="ozanalyticsui-table">
              <thead>
                <tr><th>Product Name</th><th>Batch No</th><th>Current Stock</th><th>Alert Level</th></tr>
              </thead>
              <tbody>
                {lowStockItems.map((product) => (
                  <tr key={product.id} className="ozanalyticsui-clickable-row" onClick={() => openList("Low Stock Alert")} style={{ cursor: 'pointer' }}>
                    <td>{product.product_name}</td>
                    <td>{product.batch_no || "-"}</td>
                    <td className="ozanalyticsui-stock-low">{product.quantity} {product.unit}</td>
                    <td>{product.quantity_alert || 5}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default PharmacyDashboardHome;